import {OptiselEventJson} from "./OptiselEventHistoryPage";
import {useNavigate, useParams} from "react-router";
import {Card} from "../../components/Card";
import {NavLink} from "react-router-dom";
import React, {useContext, useState} from "react";
import {useFetchAllObjs} from "../../platform/hooks/PlatformTypeScriptHooks";
import {ImpellerAssemblyJson, PartListEntryJson} from "../mechanical/ImpellerAssemblyService";
import {FanConstructionMaterialsTable} from "../../components/domain/SimulationDatasheet";
import {ErrorBoundary} from "@sentry/react";
import {AssemblyOverview} from "../../components/assembly/AssemblyOverview";
import {DatasheetComponent} from "../datasheet/DatasheetComponent";
import {InstalledFanDatasheet} from "../../business/InstalledFanDatasheet";
import {SimulateButton} from "../simulation/SimulateButton";
import {SelectionRequestJson} from "../../business/ApiJsonTypes";
import {SelectionCriteriaDownloader} from "../selection/criteriaexport/SelectionCriteriaDownloader";
import {writePreferredSelectionCriteria} from "../selection/preferredSelectionCriteriaStorage";
import {EventToIssueAttacher} from "../issues/EventToIssueAttacher";
import {HotIssueContext} from "../issues/hotssue/HotIssueContextProvider";
import {PayloadApiTypeCodes} from "./payloadTypeCodes";
import AjaxService from "../../platform/ajax/AjaxService";
import {OptiselEventNoteEditor} from "./OptiselEventNoteEditor";
import {Protected} from "../../layout/Protected";

const AUDIT_EVENT_TYPES_WITHOUT_NOTE_EDITOR = new Set([
    "issue-created",
    "issue-reference-information-updated",
    "issue-assignee-change",
    "issue-archived",
    "issue-restored-from-archive",
]);


export function ShowOptiselEventPage(props: { event: OptiselEventJson }) {


    const {id} = useParams();

    const event = useFetchAllObjs<OptiselEventJson>(`/api/optisel-event/${id}`);
    const [noteOverride, setNoteOverride] = useState<string | undefined>(undefined);


    if (event == null) {
        return <Card>
            <div className="card-body ">...</div>
        </Card>
    }

    const showNoteEditor = !AUDIT_EVENT_TYPES_WITHOUT_NOTE_EDITOR.has(event.type);
    const currentNote = noteOverride ?? event.note;

    return <>
        <div className={"d-flex flex-row-reverse"}>
            <NavLink to={"/event-history"} className={"btn btn-sm btn-secondary"}>Wróc do listy doborów</NavLink>
        </div>
        <Card>

            <div className={"d-flex justify-content-between align-items-start gap-3"}>
                <h3 className={"text-gray-500"}>{event.performerDisplayName} - {event.typeDisplayName} - {new Date(event.creationMillis).toLocaleString("pl")}</h3>
                {showNoteEditor && <OptiselEventNoteEditor
                    eventId={event.id}
                    initialNote={currentNote}
                    onNoteUpdated={setNoteOverride}
                />}
            </div>

            {event.type === "published-mechanical-impeller-assembly" && event.caseIdentification.caseNumber && <div className={"fs-6"}>
                Numer sprawy podany przy zapisie złożenia w historii: <strong>{event.caseIdentification.caseNumber}</strong>
            </div>}

            {event.persistent && <div>
                <Protected privilege={"PERM_HISTORY_AND_ISSUES_MANAGEMENT"}>
                    <EventToIssueAttacher eventId={event.id} renderer={openDialog => (<button className={"btn btn-xs btn-secondary"} onClick={openDialog}>Przypisz do sprawy</button>)}/>
                </Protected>
            </div>}


        </Card>
        {event.payloads!.map(payload => <Card additionalClassNames={"mt-2"}>
            <ErrorBoundary fallback={(errorData) => <div className={"alert alert-warning"}>BŁĄD. Nie można wyświetlić załączonej informacji typu "{payload.typeCode}". Prawdopodobnie wersja zapisana w bazie danych jest przestarzała.</div>}>
                {payload.typeCode === PayloadApiTypeCodes.LOGS && <LogsPayloadView payload={payload}/>}
                {payload.typeCode === PayloadApiTypeCodes.SELECTION_RESULTS && <SelectionResultsPayloadView payload={payload}/>}
                {payload.typeCode === PayloadApiTypeCodes.MECHANICAL_PART_LIST && <MechanicalPartListPayloadView payload={payload}/>}
                {payload.typeCode === PayloadApiTypeCodes.PHYSICAL_PARAMETERS && <PhysicalParametersPayloadView payload={payload}/>}
                {payload.typeCode === PayloadApiTypeCodes.ERROR_MESSAGE && <ErrorMessagePayloadView payload={payload}/>}
                {payload.typeCode === PayloadApiTypeCodes.MECHANICAL_ASSEMBLY_RESULTS && <MechanicalAssemblyResultsPayloadView payload={payload}/>}
                {payload.typeCode === PayloadApiTypeCodes.SIMPLE_JSON_OBJECT && <SimpleJsonObjectPayloadView payload={payload}/>}
                {payload.typeCode === PayloadApiTypeCodes.IMPELLER_ASSEMBLY_JSON && <MechanicalImpellerAssemblyPayloadView payload={payload}/>}
                {payload.typeCode === PayloadApiTypeCodes.DATASHEET_COMMENT_V1 && <DatasheetCommentPayloadView payload={payload}/>}


                {payload.typeCode === PayloadApiTypeCodes.BINARY_PDF_DATASHEET_V2 && <BinaryPdfDatasheetPayload_v2_Downloader payload={payload} eventId={event.id}/>}
                {payload.typeCode === PayloadApiTypeCodes.DRIVEWORKS_XLS_V1 && <DriveWorksXlsPayload_v1_Downloader payload={payload} eventId={event.id}/>}
                {payload.typeCode === PayloadApiTypeCodes.SELECTION_REQUEST && <SelectionCriteriaPayload_v1_PresenterAndDownloader payload={payload as unknown as SelectionRequestJson} eventId={event.id}/>}
                {payload.typeCode === PayloadApiTypeCodes.INSTALLED_FAN_DATASHEET && <DatasheetComponent datasheet={payload as unknown as InstalledFanDatasheet} memoizedDatasheetId={undefined} blueprintId={undefined} showPrintCommentInput={false} paramsForInteractiveLocatedSplChart={
                    {housingDiameterMilimeter: 2300, pwlTot: 11.1, operation: "INDUCED_DRAFT"}
                }/>}


            </ErrorBoundary>
        </Card>)}
    </>;
};

export function LogsPayloadView(props: { payload: any }) {
    const logs = props.payload.logs as string[];
    const [expanded, setExpanded] = useState(false);
    return <>
        <h3>Logi obliczeniowe</h3>
        <div className={"text-gray-400"}>(użyj paska przewijania na boki, bądź scroll+shift w celu przewijania w poziomie)</div>
        <div style={{fontFamily: 'Consolas, monospace', fontSize: "11px", maxHeight: (expanded ? undefined : "200px"), overflowY: "hidden", overflowX: "scroll", whiteSpace: "nowrap"}}>
            {!expanded && <button className={"btn btn-sm btn-primary"} onClick={() => setExpanded(true)}>Rozwiń</button>}
            {logs.map(log => <div>{log}</div>)}
        </div>
    </>;
}


export function SelectionResultsPayloadView(props: { payload: any }) {
    const selectionResults = props.payload.selectionResults as string[];
    return <>
        <h3>Rezultaty doboru</h3>
        <div>
            {selectionResults.map(resultName => <div>{resultName}
                <hr/>
            </div>)}
        </div>
    </>;
}

export function MechanicalAssemblyResultsPayloadView(props: { payload: any }) {
    const selectionResults = props.payload.resultNames as string[];
    return <>
        <h3>Rezultaty złożenie mechanicznego</h3>
        <div>
            {selectionResults.map(resultName => <div>{resultName}
                <hr/>
            </div>)}
        </div>
    </>;
}


export function MechanicalPartListPayloadView(props: { payload: any }) {
    const entries = props.payload.partListEntries as PartListEntryJson[];
    return <Protected privilege={"PERM_DW_AND_BOM"}>
        <h3>Lista materiałowa</h3>
        <div>
            <FanConstructionMaterialsTable assemblyPartList={{
                originalList: [],
                mergedList: entries
            }}/>
        </div>
    </Protected>;
}

type PhysicalParameterJson = {
    name: string,
    stringifiedValue: string
}

export function PhysicalParametersPayloadView(props: { payload: any }) {
    const parameters = props.payload.parameters as PhysicalParameterJson[];
    return <>
        <h3>Obliczone parametry</h3>
        <div>
            <table className={"table table-row-bordered table-row-gray-100 align-middle gs-0 gy-0"}>
                <thead>
                <tr className="fw-bolder text-muted">
                    <th className="w-25">Parametr</th>
                    <th className="min-w-140px">Wartość</th>
                </tr>
                </thead>
                <tbody>
                {parameters.filter(p => p.stringifiedValue != 'N/A' && p.stringifiedValue.length > 0).map(parameter => <tr>
                    <td>{parameter.name}</td>
                    <td>{parameter.stringifiedValue}</td>
                </tr>)}
                </tbody>
            </table>
        </div>
    </>;
}

export function DatasheetCommentPayloadView(props: { payload: any }) {
    const comment = props.payload.comment as string;
    return <>
        <h3>Komentarz użytkownika</h3>
        <div className={"text-gray-500"}>Komentarz wprowadzony przez użytkownika w momencie zapisu karty wentylatora:</div>
        <div style={{whiteSpace: "pre-wrap"}} className={"mt-3 fs-5"}>{comment}</div>
    </>;
}

export function ErrorMessagePayloadView(props: { payload: any }) {
    const message = props.payload.message as string;
    return <>
        <h3>Końcowy komunikat błędu</h3>
        <div>
            <h3 className={"text-warning"}>{message}</h3>
        </div>
    </>;
}

export function SimpleJsonObjectPayloadView(props: { payload: any }) {
    const json = props.payload.objectJson as string;
    const title = props.payload.title as string;
    const subCode = props.payload.subCode as string;

    return <>
        <h3>{title} (w formacie JSON)</h3>
        <div className={"text-gray-500"}>Obiekt jest prezentowany w takiej postaci ze względu na łatwość implementacji i uniknięcie kosztu transformacji na bardziej czytelny dla użytkownika format, nadal wnosząc walory użyteczne. W przyszłości kształt tego bloku może zostać lepiej sformatowany.</div>
        <div className={"text-gray-400"}>{subCode}</div>
        <div>
            <code style={{display: "block"}} className={"mt-3"}>
                <pre>{json}</pre>
            </code>
        </div>
    </>;
}

export function MechanicalImpellerAssemblyPayloadView(props: { payload: any }) {
    const impellerAssemblyJson = props.payload.impellerAssemblyJson as string;
    const impellerAssebmly = JSON.parse(impellerAssemblyJson) as ImpellerAssemblyJson;

    // The original blueprint ID may be saved, but blueprints are SHORT LIVING! so any driveworks links are likely no longer valid
    impellerAssebmly.memoizedAsBlueprintId = null;
    return <>
        <h3>Zapisane złożenie mechaniczne</h3>
        <div>
            <AssemblyOverview assembly={impellerAssebmly} detailsExpandedByDefault={true} includeMaterialTable={true} displayActionsPanel={false} dimensionalImageDescriptors={[]} resonanceReport={null}/>
        </div>
    </>
        ;
}

export function BinaryPdfDatasheetPayload_v2_Downloader(props: { payload: any, eventId: string }) {
    const [downloading, setDownloading] = useState(false);

    const download = async () => {
        setDownloading(true);
        try {
            await AjaxService.downloadAuthenticatedFile("/api/optisel-event/" + props.eventId + "/download-pdf-payload", "datasheet.pdf");
        } catch (e) {
            window.alert("Nie udało się pobrać karty wentylatora.");
        } finally {
            setDownloading(false);
        }
    };

    return <>
        <h3>Oryginalna karta wentylatora PDF (plik binarny wygenerowany przy wydruku)</h3>
        <button className={"btn btn-primary"} disabled={downloading} onClick={download}>
            {downloading ? <><i className="fas fa-spinner fa-spin me-2"/>Pobieranie…</> : "Pobierz"}
        </button>
    </>
}

export function DriveWorksXlsPayload_v1_Downloader(props: { payload: any, eventId: string }) {
    const [downloading, setDownloading] = useState(false);

    const download = async () => {
        setDownloading(true);
        try {
            await AjaxService.downloadAuthenticatedFile("/api/optisel-event/" + props.eventId + "/download-driveworks-xls-payload", "driveworks.xlsx");
        } catch (e) {
            window.alert("Nie udało się pobrać pliku DriveWorks.");
        } finally {
            setDownloading(false);
        }
    };

    return <Protected privilege={"PERM_DW_AND_BOM"}>
        <h3>DriveWorks XLS</h3>
        <button className={"btn btn-primary"} disabled={downloading} onClick={download}>
            {downloading ? <><i className="fas fa-spinner fa-spin me-2"/>Pobieranie…</> : "Pobierz XLS"}
        </button>
    </Protected>
}

export function SelectionCriteriaPayload_v1_PresenterAndDownloader(props: { payload: SelectionRequestJson, eventId: string }) {

    var navigate = useNavigate();

    const searchByThoseCriteria = () => {
        writePreferredSelectionCriteria(props.payload);
        navigate('/selection')
    }
    console.log("Found criteria for re-selecting fan: ", props.payload);
    return <>
        <h3>Ponowny dobór</h3>
        <p>Istnieje możliwość wywołania doboru wentylatorów z kryteriami celującymi w dobór tego wentylatora. Większość kryteriów zostanie ustalona na takie jak przy doborze, przy którym
            uzyskano dobór tego wentylatora, jednak wydajność i kąt łopat zostaną ustawione tak, by była największa szansa na uzyskanie doboru tego samego punktu pracy. Funkcjonalność ta może
            służyć przeprowadzaniu ponownych rewizji doboru.</p>
        <button className={"btn btn-secondary"} onClick={searchByThoseCriteria}>Szukaj nowego doboru z kryteriami tego rowiązania</button>
        <SelectionCriteriaDownloader criteria={props.payload} />

    </>
}